/* Performance Analytics — chart renderer.
 * Thin wrappers over Recharts (CLAUDE.md §2 charting standard) that consume the
 * `ChartSpec` discriminated union.  One <PAChart> covers line/area, vertical &
 * horizontal bars, grouped/stacked bars and pie/doughnut — matching every chart
 * archetype in the design mock-up. */
import {
  ResponsiveContainer, ComposedChart, BarChart, PieChart,
  Line, Area, Bar, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import type { ChartSpec } from './types';

const AXIS = { fontSize: 10, fill: '#8a8a8a' } as const;
const GRID = '#eee';
const nf = (v: number | string) => (typeof v === 'number' ? v.toLocaleString() : v);

/** Build `[{ name, s0, s1, … }]` rows from parallel label/series arrays. */
function rows(labels: string[], series: { data: (number | null)[] }[]): Record<string, number | string | null>[] {
  return labels.map((name, i) => {
    const row: Record<string, number | string | null> = { name };
    // null is preserved so line series render forecast GAPS (Recharts connectNulls=false).
    series.forEach((s, si) => { row[`s${si}`] = s.data[i] ?? null; });
    return row;
  });
}

export function PAChart({ spec }: { spec: ChartSpec }) {
  /* ── Line / area ── */
  if (spec.type === 'line') {
    const data = rows(spec.labels, spec.series);
    return (
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 6, right: 10, bottom: 0, left: -8 }}>
          <CartesianGrid stroke={GRID} vertical={false} />
          <XAxis dataKey="name" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} interval="preserveStartEnd" minTickGap={14} />
          <YAxis tick={AXIS} tickLine={false} axisLine={false} width={38}
            domain={[spec.yMin ?? 0, spec.yMax ?? 'auto']} allowDecimals={false} />
          <Tooltip formatter={(v) => nf(v as number)} contentStyle={{ fontSize: 11 }} />
          {spec.legend && <Legend wrapperStyle={{ fontSize: 10 }} iconType="plainline" />}
          {spec.series.map((s, si) =>
            s.fill ? (
              <Area key={si} type="monotone" dataKey={`s${si}`} name={s.name ?? `Series ${si + 1}`}
                stroke={s.color} strokeWidth={2} fill={s.color} fillOpacity={0.1} dot={false}
                strokeDasharray={s.dashed ? '5 3' : undefined} isAnimationActive={false} />
            ) : (
              <Line key={si} type="monotone" dataKey={`s${si}`} name={s.name ?? `Series ${si + 1}`}
                stroke={s.color} strokeWidth={2} dot={false}
                strokeDasharray={s.dashed ? '5 3' : undefined} isAnimationActive={false} />
            ),
          )}
        </ComposedChart>
      </ResponsiveContainer>
    );
  }

  /* ── Vertical / horizontal single-metric bar ── */
  if (spec.type === 'bar') {
    const data = spec.labels.map((name, i) => ({ name, v: spec.data[i] }));
    const colorAt = (i: number) => (Array.isArray(spec.color) ? spec.color[i] : spec.color);
    const horiz = spec.horizontal;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout={horiz ? 'vertical' : 'horizontal'}
          margin={{ top: 6, right: 14, bottom: 0, left: horiz ? 6 : -8 }}>
          <CartesianGrid stroke={GRID} vertical={horiz} horizontal={!horiz} />
          {horiz ? (
            <>
              <XAxis type="number" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }}
                domain={[spec.yMin ?? 0, spec.yMax ?? 'auto']} />
              <YAxis type="category" dataKey="name" tick={AXIS} tickLine={false} axisLine={false} width={108} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} interval={0}
                angle={spec.labels.length > 6 ? -18 : 0} textAnchor={spec.labels.length > 6 ? 'end' : 'middle'} height={spec.labels.length > 6 ? 46 : 24} />
              <YAxis tick={AXIS} tickLine={false} axisLine={false} width={38}
                domain={[spec.yMin ?? 0, spec.yMax ?? 'auto']} allowDecimals={false} />
            </>
          )}
          <Tooltip formatter={(v) => nf(v as number)} cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: 11 }} />
          <Bar dataKey="v" radius={horiz ? [0, 4, 4, 0] : [4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((_, i) => <Cell key={i} fill={colorAt(i)} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  /* ── Grouped / stacked multi-series bar ── */
  if (spec.type === 'groupBar') {
    const data = rows(spec.labels, spec.series);
    const horiz = spec.horizontal;
    return (
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} layout={horiz ? 'vertical' : 'horizontal'}
          margin={{ top: 6, right: 14, bottom: 0, left: horiz ? 6 : -8 }}>
          <CartesianGrid stroke={GRID} vertical={horiz} horizontal={!horiz} />
          {horiz ? (
            <>
              <XAxis type="number" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} domain={[spec.yMin ?? 0, spec.yMax ?? 'auto']} />
              <YAxis type="category" dataKey="name" tick={AXIS} tickLine={false} axisLine={false} width={108} />
            </>
          ) : (
            <>
              <XAxis dataKey="name" tick={AXIS} tickLine={false} axisLine={{ stroke: GRID }} interval={0}
                angle={spec.labels.length > 6 ? -18 : 0} textAnchor={spec.labels.length > 6 ? 'end' : 'middle'} height={spec.labels.length > 6 ? 46 : 24} />
              <YAxis tick={AXIS} tickLine={false} axisLine={false} width={38} domain={[spec.yMin ?? 0, spec.yMax ?? 'auto']} allowDecimals={false} />
            </>
          )}
          <Tooltip formatter={(v) => nf(v as number)} cursor={{ fill: 'rgba(0,0,0,0.04)' }} contentStyle={{ fontSize: 11 }} />
          {(spec.legend ?? true) && <Legend wrapperStyle={{ fontSize: 10 }} />}
          {spec.series.map((s, si) => (
            <Bar key={si} dataKey={`s${si}`} name={s.name} fill={s.color}
              stackId={spec.stacked ? 'a' : undefined}
              radius={spec.stacked ? 0 : (horiz ? [0, 3, 3, 0] : [3, 3, 0, 0])} isAnimationActive={false} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  /* ── Pie / doughnut ── */
  const data = spec.labels.map((name, i) => ({ name, value: spec.data[i] }));
  const right = (spec.legendPos ?? 'right') === 'right';
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
        <Pie data={data} dataKey="value" nameKey="name" cx={right ? '40%' : '50%'} cy="50%"
          innerRadius={spec.donut ? '52%' : 0} outerRadius="80%" isAnimationActive={false}>
          {data.map((_, i) => <Cell key={i} fill={spec.colors[i % spec.colors.length]} />)}
        </Pie>
        <Tooltip formatter={(v) => nf(v as number)} contentStyle={{ fontSize: 11 }} />
        <Legend layout={right ? 'vertical' : 'horizontal'} align={right ? 'right' : 'center'}
          verticalAlign={right ? 'middle' : 'bottom'} wrapperStyle={{ fontSize: 9.5 }} iconSize={9} />
      </PieChart>
    </ResponsiveContainer>
  );
}
